import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Inject, Injectable, LOCALE_ID, OnDestroy, inject } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LoginControllerService, UserControllerService } from "./server-api/services";
import { AuthResponse, PasswordChangeDto, Profile } from "./server-api/models";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { SideNavService } from "./core-flc/sidenav/sidenav.service";
import { MatDialog } from "@angular/material/dialog";
import { AlertComponent } from "./utils-flc/alert/alert.component";
import { TranslateService } from "@ngx-translate/core";
import { LocaleStorageService } from "./locale-storage.service";
import { RouterLinkEnum } from "./enums/router-link-enum";
import { Location } from '@angular/common';


@Injectable({ providedIn: 'root' })
export class StateService implements OnDestroy {

  //used to prevent 2 login popups after logoff
  public logoff: boolean = false;

  // BehaviorSubject to store
  private currentScreenSizeStore = new BehaviorSubject<boolean>(false);
  private userDisplayNameStore = new BehaviorSubject<string>(null);
  private userNameLoginStore = new BehaviorSubject<string>(null);
  private isAuthenticatedStore = new BehaviorSubject<boolean>(null);
  private userProfileStore = new BehaviorSubject<Profile>(null);
  private sessionId: string;

  destroyed = new Subject<void>();

  // Make store Observable
  public currentScreenSize$ = this.currentScreenSizeStore.asObservable();
  public userDisplayName$ = this.userDisplayNameStore.asObservable();
  public userNameLogin$ = this.userNameLoginStore.asObservable();
  public isAuthenticated$ = this.isAuthenticatedStore.asObservable();
  public userProfile$ = this.userProfileStore.asObservable();
  

  password: string;

  private dialog = inject(MatDialog);
  
  constructor(
    private loginService: LoginControllerService,
    private userService: UserControllerService,
    private sidenavService: SideNavService,
    private breakpointObserver: BreakpointObserver,
    @Inject(LOCALE_ID) public locale: string,
    private translate: TranslateService,
    private localStorageService: LocaleStorageService,
    private router: Router,
    private readonly location: Location
  ) {
    let displayNameMap = new Map([
      [Breakpoints.XSmall, 'Small'],
      [Breakpoints.Small, 'Small'],
      [Breakpoints.Medium, 'Large'],
      [Breakpoints.Large, 'Large'],
      [Breakpoints.XLarge, 'Large'],
    ]);

    this.breakpointObserver.observe([
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
    ]).pipe(takeUntil(this.destroyed)).subscribe(result => {
      for (const query of Object.keys(result.breakpoints)) {
        if (result.breakpoints[query]) {

          if (displayNameMap.get(query) === 'Large')
            this.setScreenSize(true);
          else this.setScreenSize(false);
        }
      }
    });   
  }
  
  setScreenSize(screenSize: boolean) {
    this.currentScreenSizeStore.next(screenSize);
  }
  getScreenSize() {
    return this.currentScreenSizeStore;
  }

  isMobileDevice() {
    var check = false;
    (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor);
    return check;
  };

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  
  getProfile() {
    return this.userProfileStore.value;
  }

  checkLogin(userName: string, password: string, rememberMe: boolean) {
    this.sidenavService.setLoading(true);
    let request = { username: userName, password: password };

    this.password = password;
    this.userNameLoginStore.next(userName);
    this.loginService.login({ body: request }).subscribe(
      response => {
        this.setLogin(response, rememberMe);
      }
      ,
      error => {
        console.error("error login: " + JSON.stringify(error));
      },
      
      )
  }

  logout()
  {
    this.loginService.logout().subscribe(() =>
    {
      this.setLogin(null, false);
    });
  }

  getAuthenticated() {
    return this.isAuthenticatedStore;
  }

  getSessionId() {
    return this.sessionId;
  }

  setLoginFromLocalStorage() {
    this.loginService.getSessionInfo().subscribe({
        next: (response) => {
          this.sessionId = response.sessionId;
          this.userProfileStore.next(response.profile);
          this.userDisplayNameStore.next(response.displayName);
          this.userNameLoginStore.next(response.displayName);
          console.log("session id local storage " + this.localStorageService.getData("sessionId"));
        },
        complete: () => {
          this.isAuthenticatedStore.next(true);
        },
        error: (error) => {
          console.error("error login: " + JSON.stringify(error));
        }});
    
   }

  setLogin(response: AuthResponse, rememberMe: boolean) {
    
    if (response && response.sessionId) {
      this.sessionId = response.sessionId;
      this.userDisplayNameStore.next(response.displayName);
      this.userProfileStore.next(response.profile);
      this.localStorageService.saveData('sessionId', response.sessionId);
      this.localStorageService.saveData('remember_me', JSON.stringify(rememberMe));
      
      this.isAuthenticatedStore.next(true);
      this.sidenavService.setLoading(false);
    }
    else {
      this.sessionId = null;
      this.localStorageService.clearData(); 
      this.userDisplayNameStore.next(null);
      this.userNameLoginStore.next(null);
      this.userProfileStore.next(null);
      this.isAuthenticatedStore.next(false);
      
      this.sidenavService.setLoading(false);
      //response is object after real try login
      if (response)
      {
        let message: string = "";
        let title: string = "";
        this.translate.get(['login.invalid', 'login.invalid_title']).subscribe((translations: string) => {
          message = translations['login.invalid'];
          title = translations['login.invalid_title'];

        });
        const alertDialog = this.dialog.open(AlertComponent, {
          data: {message: message, type: title, buttonCancel: "close" }, hasBackdrop: true }
  
      );
      alertDialog.afterClosed().subscribe(result => {
        
      });
      }
      else 
      {
        //because no sessionId change, so no catch in loginComponent
        this.router.navigate(['/']);
      }
    }
  }

  checkUserLogin(route: ActivatedRouteSnapshot, url: any): boolean {
    //when not yet authenticated do this first
    if (this.isAuthenticatedStore.getValue() === null)
    {
      this.loginService.getSessionInfo().subscribe({
        next: (response) => {
          this.sessionId = response.sessionId;
          this.userDisplayNameStore.next(response.displayName);
          this.userNameLoginStore.next(response.displayName);
          this.userProfileStore.next(response.profile);
          console.log("session id local storage " + this.localStorageService.getData("sessionId"));
        },
        complete: () => {
          this.isAuthenticatedStore.next(true);
          return this.checkItemAuth(route, url);
        },
        error: (error) => {
          console.error("error login: " + JSON.stringify(error));
        }});    
    }
    else
    {
      return this.checkItemAuth(route, url);
    }
    
  }

  checkItemAuth(route: ActivatedRouteSnapshot, url: any): boolean {
    if (this.isAuthenticated$) {
      let itemLink:RouterLinkEnum = RouterLinkEnum.getRouterLinkByLink(url);
      if (!itemLink) return false;
      if (itemLink.profiles.includes(this.userProfileStore.value))
      {
        return true;
      }
      else
      {
        return false;
      }
    }
    else
    {
      return false;
    }
  };

  changeOwnPassword(userName: string, passwordOld: string, passwordNew)
  {
    this.sidenavService.setLoading(true);
    let changeOwnPassword: PasswordChangeDto = { newPassword: passwordNew, oldPassword: passwordOld };
    
    this.userService.changeOwnPassword({
      pwChange: changeOwnPassword,
      user: undefined
    }).subscribe(
      response => {
        this.sidenavService.setLoading(false);
        window.alert("Password has changed successfully");
        this.checkLogin(userName, passwordNew, false);   
        this.location.back();
      }
      ,
      error => {
        this.sidenavService.setLoading(false);
        window.alert("There is a problem with changing password");
        this.router.navigate([RouterLinkEnum.CHANGE_PASSWORD.link]);
      },
      () => {
        this.sidenavService.setLoading(false);
      }
      )
  }

  changeUserPassword(id: string, userName: string, passwordNew)
  {
    this.sidenavService.setLoading(true);
    let changeUserPassword: PasswordChangeDto = { newPassword: passwordNew, userId: id };

    
    this.userService.changeUserPassword({
      pwChange: changeUserPassword,
      user: undefined
    }).subscribe(
      response => {
        this.sidenavService.setLoading(false);
        window.alert("Password has changed successfully");
      }
      ,
      error => {
        this.sidenavService.setLoading(false);
        console.error("error login: " + JSON.stringify(error));
      },
      () => {
        this.sidenavService.setLoading(false);
        window.location.reload();
      }
      )
  }
}
